import MaterialReactTable from 'material-react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button, Col, Container, FormGroup, Input, Label, Row, Spinner,
} from 'reactstrap'
import Select from '../../components/Template/Input/Select'
import oikosApi from '../../services/oikos'
import { getRegs, selectValues } from '../../utils/fc'
import { getOikosFileUrl } from '../../utils/linksOikos/link'
import { notifyError, notifySuccess } from '../../utils/notify'
import qs from 'qs'

const formatApiError = (err) => {
  const d = err?.data
  if (typeof d === 'string' && d.trim()) return d.trim()
  if (d?.message) return String(d.message)
  if (d?.error) return String(d.error)
  if (Array.isArray(d) && d[0]) return String(d[0])
  if (err?.message) return err.message
  return null
}

const escapeOikosSqlPayload = (data) => {
  const out = {}
  Object.keys(data || {}).forEach((key) => {
    const value = data[key]
    out[key] = typeof value === 'string' ? value.replace(/'/g, "''") : value
  })
  return out
}

const nowSql = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const parsePfIds = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.map(Number).filter(n => !Number.isNaN(n))
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed.map(item => Number(item?.value ?? item)).filter(n => !Number.isNaN(n))
      }
    } catch (e) {
      return String(raw).split(/[,;]/).map(s => Number(s.trim())).filter(n => !Number.isNaN(n))
    }
  }
  return []
}

const parseServicoLabels = (raw) => {
  if (!raw) return []
  return String(raw)
    .split(';')
    .map(s => s.trim())
    .filter(Boolean)
}

const formatServicos = (raw) => {
  const labels = parseServicoLabels(raw)
  return labels.length ? labels.join(', ') : ''
}

const anexoNome = (row) => {
  const leaf = String(row?.FileLeafRef || row?.LinkFilename || '').trim()
  if (leaf) return leaf
  const path = String(row?.anexos || '').trim()
  if (!path) return ''
  const parts = path.split(/[/\\]/)
  return parts[parts.length - 1] || path
}

const anexoUrl = (row) => getOikosFileUrl(row?.arquivoCaminho)

const isFlagOn = (value) => value === 1 || value === '1' || value === true || value === 'true'

const parseArquivoMeta = (desc) => {
  const raw = String(desc || '').trim()
  if (!raw.startsWith('{')) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (e) {
    return null
  }
}

const formatSqlDate = (value) => {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return ''
  return `${match[3]}/${match[2]}/${match[1]}`
}

const isLinkActive = (row) => row && Number(row.projAtestadoTeste_ativo) === 1

const sameIdSp = (a, b) => String(a || '') === String(b || '')

function AtestadoDetailPanel({ row, busy, onSaveNota }) {
  const [texto, setTexto] = useState(row?.nota || '')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setTexto(row?.nota || '')
    setDirty(false)
  }, [row?._key, row?.nota])

  const bloco = (label, value) => (
    <div>
      <div className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
        {String(value || '').trim() || '—'}
      </div>
    </div>
  )

  return (
    <div style={{ padding: '8px 16px 16px' }} onClick={e => e.stopPropagation()}>
      <Row>
        <Col md={6}>{bloco('Descrição', row?.descricao)}</Col>
        <Col md={3}>{bloco('Setor', row?.setor)}</Col>
        <Col md={3}>{bloco('Segmento', row?.segmento)}</Col>
      </Row>
      <div className="mt-3">
        <div className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
          Anotações
        </div>
        <Input
          type="textarea"
          rows={4}
          value={texto}
          disabled={busy}
          placeholder="Comentários específicos deste atestado..."
          onChange={(e) => {
            setTexto(e.target.value)
            setDirty(true)
          }}
          style={{ fontSize: 13 }}
        />
        <div className="d-flex align-items-center mt-2" style={{ gap: 12 }}>
          <Button
            color="primary"
            size="sm"
            disabled={busy || !dirty}
            onClick={async () => {
              const ok = await onSaveNota(texto)
              if (ok !== false) setDirty(false)
            }}
          >
            {busy ? 'Salvando...' : 'Salvar anotação'}
          </Button>
        </div>
      </div>
    </div>
  )
}

const cellText = (value, lines = 3) => {
  const v = String(value || '').trim()
  if (!v) return <span className="text-muted">—</span>
  return (
    <span
      title={v}
      style={{
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        fontSize: 12,
        lineHeight: 1.3,
        wordBreak: 'break-word',
      }}
    >
      {v}
    </span>
  )
}

const tableSelectProps = {
  classNamePrefix: 'select2-selection',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999, minWidth: 280 }),
    container: (base) => ({ ...base, width: '100%' }),
    control: (base) => ({
      ...base,
      minHeight: 30,
      fontSize: 12,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 4px',
    }),
    singleValue: (base) => ({
      ...base,
      maxWidth: 'calc(100% - 8px)',
      fontSize: 12,
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: 12,
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontSize: 11,
      padding: '1px 4px',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 28,
    }),
    clearIndicator: (base) => ({ ...base, padding: 2 }),
    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
  },
}

const rowKey = (row) => String(row?.IdSP || row?.id || '')

function VincularAtestados() {
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState(null)
  const [atestados, setAtestados] = useState([])
  const [links, setLinks] = useState([])
  const [allLinks, setAllLinks] = useState([])
  const [projOptions, setProjOptions] = useState([])
  const [equipeOptions, setEquipeOptions] = useState([])
  const [filter, setFilter] = useState('pendentes')
  const [drafts, setDrafts] = useState({})
  const topScrollRef = useRef(null)
  const tableScrollRef = useRef(null)
  const syncingScroll = useRef(false)
  const [hScrollWidth, setHScrollWidth] = useState(0)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [xspRes, linkRows, allLinkRows, projs, pessoas, arquivoRows] = await Promise.all([
        oikosApi.get(`/?${qs.stringify({
          action: 'get',
          table: 'xsp_atestado',
        })}`),
        getRegs('projAtestadoTeste', {}, {
          '(SELECT proj_nome FROM proj WHERE proj.proj_id = projAtestadoTeste.proj_id)': 'proj_nome',
          '(SELECT contrato_num FROM contrato WHERE contrato.contrato_id = projAtestadoTeste.contrato_id)': 'contrato_num',
        }),
        getRegs('projAtestadoTeste', {}, {
          projAtestadoTeste_id: 'projAtestadoTeste_id',
          IdSP: 'IdSP',
          proj_id: 'proj_id',
          contrato_id: 'contrato_id',
          projAtestadoTeste_equipe: 'projAtestadoTeste_equipe',
          projAtestadoTeste_ativo: 'projAtestadoTeste_ativo',
          projAtestadoTeste_anotacao: 'projAtestadoTeste_anotacao',
          projAtestadoTeste_crea: 'projAtestadoTeste_crea',
          projAtestadoTeste_crbio: 'projAtestadoTeste_crbio',
        }, false),
        getRegs('proj', {}, {
          '(SELECT contrato_num FROM contrato WHERE contrato.contrato_id = proj.contrato_id)': 'contrato_num',
          '(SELECT pj_nomefanta FROM pj WHERE pj.pj_id = (SELECT contrato_contratante FROM contrato WHERE contrato.contrato_id = proj.contrato_id))': 'pj_nomefanta',
        }),
        selectValues('pf', 'pf_id', 'pf_nomeExibicao'),
        getRegs('arquivo', { arquivo_tabRef: 'xsp_atestado' }),
      ])

      const arquivoByIdSp = {}
      ;(Array.isArray(arquivoRows) ? arquivoRows : []).forEach((a) => {
        const idSp = String(a?.arquivo_tabId || '').trim()
        if (!idSp || !a?.arquivo_caminho) return
        if (!arquivoByIdSp[idSp] || Number(a.arquivo_id) > Number(arquivoByIdSp[idSp].arquivo_id)) {
          arquivoByIdSp[idSp] = a
        }
      })
      const notaByIdSp = {}
      const creaByIdSp = {}
      const crbioByIdSp = {}
      ;(Array.isArray(allLinkRows) ? allLinkRows : []).forEach((l) => {
        const idSp = String(l?.IdSP || '').trim()
        if (!idSp) return
        const texto = String(l?.projAtestadoTeste_anotacao || '').trim()
        if (texto && (isLinkActive(l) || !notaByIdSp[idSp])) notaByIdSp[idSp] = texto
        if (l.projAtestadoTeste_crea != null && l.projAtestadoTeste_crea !== '' && (isLinkActive(l) || creaByIdSp[idSp] == null)) {
          creaByIdSp[idSp] = isFlagOn(l.projAtestadoTeste_crea)
        }
        if (l.projAtestadoTeste_crbio != null && l.projAtestadoTeste_crbio !== '' && (isLinkActive(l) || crbioByIdSp[idSp] == null)) {
          crbioByIdSp[idSp] = isFlagOn(l.projAtestadoTeste_crbio)
        }
      })
      setAtestados(
        (Array.isArray(xspRes.data) ? xspRes.data : []).map((row) => {
          const idSp = String(row?.IdSP || '').trim()
          const arquivo = arquivoByIdSp[idSp] || null
          const meta = parseArquivoMeta(arquivo?.arquivo_desc)
          const crea = creaByIdSp[idSp] != null
            ? creaByIdSp[idSp]
            : (meta && meta.crea != null ? isFlagOn(meta.crea) : isFlagOn(row.CREA))
          const crbio = crbioByIdSp[idSp] != null
            ? crbioByIdSp[idSp]
            : (meta && meta.crbio != null ? isFlagOn(meta.crbio) : isFlagOn(row.CRBIO))
          return {
            ...row,
            arquivoId: arquivo?.arquivo_id || null,
            arquivoCaminho: arquivo?.arquivo_caminho || '',
            crea,
            crbio,
            nota: notaByIdSp[idSp] || '',
          }
        }),
      )
      setLinks(Array.isArray(linkRows) ? linkRows : [])
      setAllLinks(Array.isArray(allLinkRows) ? allLinkRows : [])
      setProjOptions(
        (Array.isArray(projs) ? projs : []).map(p => ({
          value: p.proj_id,
          label: p.contrato_num
            ? `${p.proj_nome || 'Sem nome'} (${p.contrato_num})`
            : (p.proj_nome || `Projeto #${p.proj_id}`),
          proj_nome: p.proj_nome,
          contrato_id: p.contrato_id || null,
          pj_nomefanta: p.pj_nomefanta || null,
        })),
      )

      const people = (Array.isArray(pessoas) ? pessoas : [])
        .filter(o => o.value && String(o.label || '').trim())
        .sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR'))
      setEquipeOptions(people)
    } catch (err) {
      console.error('VincularAtestados load error:', err)
      notifyError('Não foi possível carregar atestados ou vínculos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const getDraft = useCallback((key) => (
    drafts[key] || { projs: [], equipe: [] }
  ), [drafts])

  const setDraft = useCallback((key, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { projs: [], equipe: [] }),
        ...patch,
      },
    }))
  }, [])

  const tableRows = useMemo(() => {
    const rows = atestados.map((row) => {
      const key = rowKey(row)
      const draft = getDraft(key)
      const activeLinks = links.filter(l => isLinkActive(l) && sameIdSp(l.IdSP, row.IdSP))
      const vinculado = activeLinks.length > 0
      const linkedProjs = activeLinks.map((l) => (
        projOptions.find(o => Number(o.value) === Number(l.proj_id)) || {
          value: l.proj_id,
          label: l.proj_nome
            ? (l.contrato_num ? `${l.proj_nome} (${l.contrato_num})` : l.proj_nome)
            : `Projeto #${l.proj_id}`,
          contrato_id: l.contrato_id,
        }
      ))
      const equipeFromLink = parsePfIds(activeLinks[0]?.projAtestadoTeste_equipe)
      const linkedEquipe = equipeFromLink.map(id => (
        equipeOptions.find(o => Number(o.value) === id) || { value: id, label: `PF #${id}` }
      ))
      return {
        ...row,
        _key: key,
        vinculado,
        activeLinks,
        linkedProjs,
        draftProjs: draft.projs,
        draftEquipe: draft.equipe,
        linkedEquipe,
        servico: formatServicos(row.Servi_x00e7_os),
        descricao: row.Objeto || '',
        setor: row.Setor || '',
        segmento: row.Segmento || '',
        empresa: row.Empresa || '',
        equipeSp: String(row.jj8n || '').trim(),
        anexo: anexoNome(row),
        anexoHref: anexoUrl(row),
        dtInicio: formatSqlDate(row.Datainicio),
        dtFim: formatSqlDate(row.Datadefim),
        crea: isFlagOn(row.crea),
        crbio: isFlagOn(row.crbio),
        nota: row.nota || '',
        vinculadoEm: activeLinks
          .map(l => l.projAtestadoTeste_dtCria)
          .filter(Boolean)
          .sort()
          .slice(-1)[0] || null,
      }
    })

    if (filter === 'pendentes') return rows.filter(r => !r.vinculado)
    if (filter === 'vinculados') return rows.filter(r => r.vinculado)
    return rows
  }, [atestados, filter, getDraft, projOptions, equipeOptions, links])

  const saveLink = useCallback(async (row) => {
    if (row.vinculado) return
    const draft = getDraft(row._key)
    const selectedProjs = Array.isArray(draft.projs) ? draft.projs.filter(p => p?.value) : []
    if (!selectedProjs.length) {
      notifyError('Selecione ao menos um projeto antes de confirmar.')
      return
    }
    if (!row.IdSP) {
      notifyError('Não foi possível identificar o atestado (sem IdSP).')
      return
    }

    const equipe = draft.equipe || []
    const equipeJson = JSON.stringify(equipe.map(o => o.value))
    setSavingId(row._key)
    try {
      for (const projOption of selectedProjs) {
        const projId = Number(projOption.value)
        const inactive = (allLinks || []).find(l => (
          !isLinkActive(l)
          && sameIdSp(l.IdSP, row.IdSP)
          && Number(l.proj_id) === projId
        ))
        const payload = {
          IdSP: row.IdSP,
          proj_id: projId,
          contrato_id: projOption.contrato_id || null,
          projAtestadoTeste_equipe: equipeJson,
          projAtestadoTeste_anotacao: row.nota || '',
          projAtestadoTeste_crea: row.crea ? 1 : 0,
          projAtestadoTeste_crbio: row.crbio ? 1 : 0,
          projAtestadoTeste_ativo: 1,
          projAtestadoTeste_dtMod: nowSql(),
        }
        if (inactive?.projAtestadoTeste_id) {
          const res = await oikosApi.post('/', {
            action: 'update',
            table: 'projAtestadoTeste',
            data: escapeOikosSqlPayload({
              ...payload,
              projAtestadoTeste_id: inactive.projAtestadoTeste_id,
            }),
          })
          if (!res?.ok && res?.problem) throw res
        } else {
          const res = await oikosApi.post('/', {
            action: 'insert',
            table: 'projAtestadoTeste',
            data: escapeOikosSqlPayload({
              ...payload,
              projAtestadoTeste_dtCria: nowSql(),
            }),
          })
          const savedId = res?.data?.[0]?.projAtestadoTeste_id
          if (!savedId && !res?.ok && res?.problem) throw res
          if (!savedId && res?.data?.error) throw new Error(String(res.data.error))
        }
      }

      notifySuccess('Atestado vinculado ao(s) projeto(s).')
      setDraft(row._key, { projs: [], equipe: [] })
      await loadData()
    } catch (err) {
      console.error('VincularAtestados save error:', err)
      const detail = formatApiError(err)
      notifyError(
        detail
          ? `Não foi possível salvar o vínculo: ${detail}`
          : 'Não foi possível salvar o vínculo.',
      )
    } finally {
      setSavingId(null)
    }
  }, [getDraft, setDraft, loadData, allLinks])

  const updateEquipe = useCallback(async (row, equipe) => {
    const ids = (row.activeLinks || []).map(l => l.projAtestadoTeste_id).filter(Boolean)
    if (!ids.length) return
    setSavingId(row._key)
    try {
      const equipeJson = JSON.stringify((equipe || []).map(o => o.value))
      for (const id of ids) {
        const res = await oikosApi.post('/', {
          action: 'update',
          table: 'projAtestadoTeste',
          data: escapeOikosSqlPayload({
            projAtestadoTeste_id: id,
            projAtestadoTeste_equipe: equipeJson,
            projAtestadoTeste_dtMod: nowSql(),
          }),
        })
        if (!res?.ok && res?.problem) throw res
      }
      await loadData()
    } catch (err) {
      console.error('VincularAtestados update equipe error:', err)
      notifyError('Não foi possível atualizar a equipe.')
    } finally {
      setSavingId(null)
    }
  }, [loadData])

  const unlink = useCallback(async (row) => {
    const ids = (row.activeLinks || []).map(l => l.projAtestadoTeste_id).filter(Boolean)
    if (!ids.length) return
    setSavingId(row._key)
    try {
      for (const id of ids) {
        const res = await oikosApi.post('/', {
          action: 'update',
          table: 'projAtestadoTeste',
          data: {
            projAtestadoTeste_id: id,
            projAtestadoTeste_ativo: 0,
            projAtestadoTeste_dtMod: nowSql(),
          },
        })
        if (!res?.ok && res?.problem) throw res
      }
      notifySuccess('Atestado desvinculado.')
      setDraft(row._key, { projs: [], equipe: [] })
      await loadData()
    } catch (err) {
      console.error('VincularAtestados unlink error:', err)
      const detail = formatApiError(err)
      notifyError(
        detail
          ? `Não foi possível desvincular: ${detail}`
          : 'Não foi possível desvincular o atestado.',
      )
    } finally {
      setSavingId(null)
    }
  }, [setDraft, loadData])

  useEffect(() => {
    const el = tableScrollRef.current
    if (!el) return undefined
    const update = () => setHScrollWidth(el.scrollWidth || 0)
    update()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    if (ro) ro.observe(el)
    const table = el.querySelector('table')
    if (ro && table) ro.observe(table)
    return () => {
      if (ro) ro.disconnect()
    }
  }, [loading, tableRows.length, filter])

  const upsertLinkFields = useCallback(async (row, fields) => {
    const rowsForSp = (allLinks || []).filter(l => sameIdSp(l.IdSP, row.IdSP))
    if (rowsForSp.length) {
      for (const link of rowsForSp) {
        if (!link.projAtestadoTeste_id) continue
        const res = await oikosApi.post('/', {
          action: 'update',
          table: 'projAtestadoTeste',
          data: escapeOikosSqlPayload({
            projAtestadoTeste_id: link.projAtestadoTeste_id,
            ...fields,
            projAtestadoTeste_dtMod: nowSql(),
          }),
        })
        if (!res?.ok && res?.problem) throw res
      }
      return
    }
    const res = await oikosApi.post('/', {
      action: 'insert',
      table: 'projAtestadoTeste',
      data: escapeOikosSqlPayload({
        IdSP: row.IdSP,
        ...fields,
        projAtestadoTeste_ativo: 0,
        projAtestadoTeste_dtCria: nowSql(),
        projAtestadoTeste_dtMod: nowSql(),
      }),
    })
    if (!res?.ok && res?.problem) throw res
    if (res?.data?.error) throw new Error(String(res.data.error))
  }, [allLinks])

  const saveNota = useCallback(async (row, texto) => {
    if (!row?.IdSP) {
      notifyError('Não foi possível identificar o atestado (sem IdSP).')
      return false
    }
    setSavingId(row._key)
    try {
      await upsertLinkFields(row, { projAtestadoTeste_anotacao: texto })
      notifySuccess('Anotação salva.')
      setAtestados((prev) => prev.map((item) => (
        sameIdSp(item.IdSP, row.IdSP)
          ? { ...item, nota: texto }
          : item
      )))
      await loadData()
      return true
    } catch (err) {
      console.error('VincularAtestados save nota error:', err)
      const detail = formatApiError(err)
      notifyError(
        detail
          ? `Não foi possível salvar a anotação: ${detail}`
          : 'Não foi possível salvar a anotação. Rode o ALTER em projAtestadoTeste.',
      )
      return false
    } finally {
      setSavingId(null)
    }
  }, [loadData, upsertLinkFields])

  const updateFlags = useCallback(async (row, patch) => {
    if (!row?.IdSP) {
      notifyError('Não foi possível identificar o atestado (sem IdSP).')
      return
    }
    const crea = patch.crea != null ? !!patch.crea : !!row.crea
    const crbio = patch.crbio != null ? !!patch.crbio : !!row.crbio
    setSavingId(row._key)
    try {
      await upsertLinkFields(row, {
        projAtestadoTeste_crea: crea ? 1 : 0,
        projAtestadoTeste_crbio: crbio ? 1 : 0,
      })
      setAtestados((prev) => prev.map((item) => (
        sameIdSp(item.IdSP, row.IdSP)
          ? { ...item, crea, crbio }
          : item
      )))
      await loadData()
    } catch (err) {
      console.error('VincularAtestados update flags error:', err)
      notifyError('Não foi possível salvar CREA/CRBIO. Rode o ALTER em projAtestadoTeste.')
    } finally {
      setSavingId(null)
    }
  }, [loadData, upsertLinkFields])

  const columns = useMemo(() => [
    {
      accessorKey: 'servico',
      header: 'Serviço',
      size: 240,
      Cell: ({ cell }) => cellText(cell.getValue(), 3),
    },
    {
      accessorKey: 'dtInicio',
      header: 'Início',
      size: 88,
      Cell: ({ cell }) => cellText(cell.getValue(), 1),
    },
    {
      accessorKey: 'dtFim',
      header: 'Fim',
      size: 88,
      Cell: ({ cell }) => cellText(cell.getValue(), 1),
    },
    {
      accessorKey: 'empresa',
      header: 'Empresa',
      size: 130,
      Cell: ({ cell }) => cellText(cell.getValue(), 2),
    },
    {
      id: 'crea_crbio',
      header: 'CREA / CRBio',
      size: 92,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingId === r._key
        return (
          <div onClick={e => e.stopPropagation()} style={{ fontSize: 11, lineHeight: 1.5 }}>
            <label className="mb-0 d-flex align-items-center" style={{ gap: 4, cursor: busy ? 'wait' : 'pointer' }}>
              <input
                type="checkbox"
                checked={!!r.crea}
                disabled={busy}
                onChange={(e) => updateFlags(r, { crea: e.target.checked })}
              />
              CREA
            </label>
            <label className="mb-0 d-flex align-items-center" style={{ gap: 4, cursor: busy ? 'wait' : 'pointer' }}>
              <input
                type="checkbox"
                checked={!!r.crbio}
                disabled={busy}
                onChange={(e) => updateFlags(r, { crbio: e.target.checked })}
              />
              CRBio
            </label>
          </div>
        )
      },
    },
    {
      id: 'vincular_projeto',
      header: 'Vincular Projeto',
      size: 240,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingId === r._key
        if (r.vinculado) {
          const nome = (r.linkedProjs || []).map(p => p.label).join(', ')
          return cellText(nome, 3)
        }
        return (
          <div style={{ width: '100%', paddingLeft: 2 }} onClick={e => e.stopPropagation()}>
            <Select
              {...tableSelectProps}
              isMulti
              isSearchable
              isClearable
              isDisabled={busy}
              placeholder="Selecionar projeto(s)..."
              options={projOptions}
              value={r.draftProjs}
              onChange={(opts) => {
                setDraft(r._key, { projs: opts || [] })
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'equipe',
      header: 'Equipe',
      size: 280,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingId === r._key
        const value = r.vinculado
          ? (r.draftEquipe?.length ? r.draftEquipe : r.linkedEquipe)
          : (r.draftEquipe || [])
        return (
          <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select
                  {...tableSelectProps}
                  isMulti
                  isSearchable
                  isClearable
                  isDisabled={busy}
                  placeholder="Selecionar equipe..."
                  options={equipeOptions}
                  value={value}
                  onChange={(opts) => {
                    const next = opts || []
                    setDraft(r._key, { equipe: next })
                    if (r.vinculado) updateEquipe(r, next)
                  }}
                />
              </div>
              <div
                title={r.equipeSp || 'Sem equipe no atestado'}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  lineHeight: 1.3,
                  color: '#6c757d',
                  paddingTop: 4,
                }}
              >
                <div style={{ fontWeight: 600, color: '#495057', marginBottom: 2 }}>equipe original</div>
                {r.equipeSp || '—'}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'anexo',
      header: 'Anexo',
      size: 120,
      Cell: ({ row }) => {
        const r = row.original
        if (!r.anexo) return <span className="text-muted">—</span>
        return (
          <div onClick={e => e.stopPropagation()} style={{ fontSize: 11, lineHeight: 1.3 }}>
            <div title={r.anexo} style={{ wordBreak: 'break-word', marginBottom: 2 }}>
              <i className="ri-attachment-2 mr-1" />
              {r.anexo}
            </div>
            {r.anexoHref ? (
              <a
                href={r.anexoHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 11, lineHeight: 1.2 }}
              >
                Ver
              </a>
            ) : null}
          </div>
        )
      },
    },
    {
      id: 'acoes',
      header: 'Ações',
      size: 72,
      Cell: ({ row }) => {
        const r = row.original
        const busy = savingId === r._key
        if (r.vinculado) {
          return (
            <div onClick={e => e.stopPropagation()}>
              <Button
                color="link"
                size="sm"
                className="p-0 text-danger"
                style={{ fontSize: 10, lineHeight: 1.2 }}
                disabled={busy}
                onClick={() => unlink(r)}
              >
                Desvinc.
              </Button>
            </div>
          )
        }
        if (!r.draftProjs?.length) {
          return <span className="text-muted" style={{ fontSize: 11 }}>—</span>
        }
        return (
          <div onClick={e => e.stopPropagation()}>
            <Button
              color="primary"
              size="sm"
              style={{ fontSize: 10, padding: '1px 5px', lineHeight: 1.2 }}
              disabled={busy}
              onClick={() => saveLink(r)}
            >
              {busy ? '...' : 'Vinc.'}
            </Button>
          </div>
        )
      },
    },
  ], [savingId, projOptions, equipeOptions, setDraft, saveLink, unlink, updateEquipe, updateFlags])

  return (
    <Container fluid style={{ paddingTop: '200px' }}>
      <Row className="mb-3 align-items-end">
        <Col md={8}>
          <h4 className="mb-1">Vincular Atestados → Projetos</h4>
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            Lista os atestados do SharePoint (`xsp_atestado`) e grava o vínculo em `projAtestadoTeste`.
            Um atestado pode ir para vários projetos; a mesma pessoa pode estar em vários atestados.
            Expanda a linha para ver descrição, setor, segmento e anotações. CREA/CRBio vêm do atestado e podem ser alterados.
          </p>
        </Col>
        <Col md={4} className="text-md-right">
          <FormGroup className="mb-0">
            <Label className="mr-2 mb-0">Filtro</Label>
            <Input
              type="select"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ maxWidth: 220, display: 'inline-block' }}
            >
              <option value="pendentes">Somente pendentes</option>
              <option value="vinculados">Somente vinculados</option>
              <option value="todos">Todos</option>
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="d-flex align-items-center text-muted py-4">
          <Spinner size="sm" className="mr-2" /> Carregando...
        </div>
      ) : (
        <>
          <div
            ref={topScrollRef}
            onScroll={(e) => {
              if (syncingScroll.current) return
              syncingScroll.current = true
              if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.target.scrollLeft
              syncingScroll.current = false
            }}
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              height: 14,
              marginBottom: 4,
            }}
          >
            <div style={{ width: Math.max(hScrollWidth, 1), height: 1 }} />
          </div>
          <MaterialReactTable
            columns={columns}
            data={tableRows}
            layoutMode="grid"
            enableColumnActions={false}
            enableColumnFilters
            enableGlobalFilter
            enableExpanding
            enablePagination={tableRows.length > 25}
            initialState={{
              density: 'compact',
              pagination: { pageSize: 25, pageIndex: 0 },
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: { width: '100%' },
            }}
            muiTableProps={{
              sx: {
                tableLayout: 'fixed',
                minWidth: 1500,
                width: '100%',
              },
            }}
            muiTableContainerProps={{
              ref: tableScrollRef,
              onScroll: (e) => {
                if (syncingScroll.current) return
                syncingScroll.current = true
                if (topScrollRef.current) topScrollRef.current.scrollLeft = e.target.scrollLeft
                syncingScroll.current = false
              },
              sx: {
                overflowX: 'auto',
                width: '100%',
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                fontSize: 12,
                px: 0.75,
                py: 0.5,
                overflow: 'hidden',
                verticalAlign: 'top',
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                fontSize: 12,
                px: 0.75,
                py: 0.5,
                whiteSpace: 'nowrap',
              },
            }}
            displayColumnDefOptions={{
              'mrt-row-expand': {
                size: 36,
                header: '',
              },
            }}
            renderDetailPanel={({ row }) => (
              <AtestadoDetailPanel
                key={row.original._key}
                row={row.original}
                busy={savingId === row.original._key}
                onSaveNota={(texto) => saveNota(row.original, texto)}
              />
            )}
          />
        </>
      )}
    </Container>
  )
}

export default VincularAtestados
